/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { FullRecruitment, getSampleProjectUrl } from 'entities';

import { Button, Column, Row } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { UserAvatar } from 'components/UserAvatar';

import {
  CircleDollarIcon,
  EarthIcon,
  FileSearchIcon,
  MaleFemaleIcon,
  TargetIcon,
  UserIcon,
} from 'components/Icons';

import { StartRecruitment } from './StartButton';
import { RecruitmentSampleStats } from './SampleStats';
import { RecruitmentTaskDate } from './TaskDate';

import { useRecruitmentModal } from './context';

import { truncateText } from 'utils/text';

const renderers: Array<{
  getLabel: (t: TFunction) => string;
  property: keyof FullRecruitment;
  extractor?: (issue: FullRecruitment & { t: TFunction }) => ReactNode;
  icon: ReactNode;
  spanAllColumns?: boolean;
  alignItems?: string;
}> = [
  {
    getLabel: (t) => t('project'),
    property: 'project',
    icon: <FileSearchIcon />,
    alignItems: 'center',
    extractor: ({ projectData, project }) => (
      <Button
        variant="ghost"
        className="p-0.5 hover:bg-primary-foreground/70"
        to="projectProfile"
        params={{ projectId: project }}
        tooltipContent={projectData.name.length > 25 ? projectData.name : null}
      >
        {truncateText(projectData.name, 25)}
      </Button>
    ),
  },
  {
    getLabel: (t) => t('client'),
    property: 'projectData',
    icon: <UserIcon />,
    extractor: ({ projectData }) => projectData.clientName,
  },
  {
    getLabel: (t) => `Sample ${t('project')}`,
    property: 'sampleId',
    icon: <FileSearchIcon />,
    extractor: ({ sampleId }) => (
      <Button
        size="fit"
        variant="ghost"
        className="p-0.5 text-sm hover:bg-primary-foreground/70"
        href={getSampleProjectUrl(sampleId, 'stats')}
        linkTarget={sampleId}
      >
        {sampleId}
      </Button>
    ),
  },
  {
    getLabel: (t) => t('country'),
    property: 'country',
    icon: <EarthIcon />,
  },
  {
    getLabel: (t) => t('age-range'),
    property: 'ageRange',
    icon: <TargetIcon />,
    extractor: ({ ageRange: { from, to } }) => `${from} - ${to}`,
  },
  {
    getLabel: (t) => t('gender'),
    property: 'gender',
    icon: <MaleFemaleIcon />,
  },
  {
    getLabel: (t) => t('region'),
    property: 'region',
    icon: <EarthIcon />,
  },
  {
    getLabel: (t) => t('selIncome'),
    property: 'selIncome',
    icon: <CircleDollarIcon />,
  },
  {
    getLabel: (t) => t('completes-necessary'),
    property: 'completesNecessary',
    icon: <FileSearchIcon />,
  },
];

export function MainInfo() {
  const { t } = useLang();

  const { recruitment } = useRecruitmentModal();

  return (
    <Column gap="6">
      <div className="grid grid-cols-2 gap-4 small_tablet:grid-cols-1">
        {renderers.map(({ getLabel, icon, property, extractor, spanAllColumns, alignItems }) => (
          <PropertyWithIcon
            containerClassName={spanAllColumns ? 'col-span-2 small_tablet:col-span-1' : undefined}
            key={property}
            gap="3"
            icon={icon}
            name={getLabel(t)}
            value={extractor?.({ ...recruitment, t }) ?? (recruitment[property] as string)}
            alignItems={alignItems as any}
          />
        ))}

        <RecruitmentTaskDate />
      </div>

      <RecruitmentSampleStats />

      <Column>
        <Row className="font-semibold">
          <UserAvatar user={recruitment.requestedBy} size={36} />
          {t('request-comments')}
        </Row>

        <Column className="rounded-lg bg-muted p-2">
          {recruitment.requestComment
            .split('\n')
            .filter(Boolean)
            .map((text) => (
              <Row className="px-2" key={text}>
                {text}
                <br />
              </Row>
            ))}
        </Column>
      </Column>

      <StartRecruitment />
    </Column>
  );
}
